import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { GapSettings } from '../../config/gapsettings';
import { RegistrationService } from '../../services/registration.service';

/**
 * Resolves the effective disable_nhs_login flag.
 * Precedence: GapSettings (Dexie-backed) → localStorage fallback → '0'.
 * Mirrors CreateAccountRedirectGuard and DataService.disable_nhs_login.
 */
function isNhsLoginDisabled(gapsettings: GapSettings): boolean {
  const fromSettings = gapsettings.disable_nhs_login;
  if (fromSettings === '1' || fromSettings === '0') {
    return fromSettings === '1';
  }
  // Fallback parity with legacy direct localStorage reads
  return localStorage.getItem('disable_nhs_login') === '1';
}

/**
 * nhsAuthGuard — protects NHS-specific routes (e.g. /nhs-callback, NHS login entry).
 *
 * Allows navigation only when:
 *   1. NHS login is enabled (disable_nhs_login !== '1'), and
 *   2. User state matches the protected route expectation.
 *
 * For public NHS entry points (login via NHS before authentication) the guard
 * allows unauthenticated access when NHS is enabled. For post-auth NHS flows
 * the caller can add additional auth checks; this guard does NOT duplicate
 * AuthGuard.canAccessProtectedRoute() — it only enforces the NHS feature flag.
 *
 * When blocked:
 *   - If NHS is disabled → redirect to /login (email fallback)
 *   - If auth required but not logged in and route expects auth → /guest
 *
 * Uses inject() functional pattern (not constructor).
 */
export const nhsAuthGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const gapsettings = inject(GapSettings);
  const router = inject(Router);

  const disable = isNhsLoginDisabled(gapsettings);

  // Also respect direct localStorage parity for QA/manual overrides
  const disableViaStorage = localStorage.getItem('disable_nhs_login') ?? '0';
  // GapSettings is authoritative; localStorage check above keeps guard correct even before Dexie hydration
  const isDisabled = disable || disableViaStorage === '1';

  if (isDisabled) {
    // NHS disabled → force email path. Preserve query params via UrlTree.
    return router.createUrlTree(['/login']);
  }

  // If the route data marks it as requiring authentication, enforce it.
  // Default: NHS public entry is allowed without login.
  const requiresAuth = route.data?.['requiresAuth'] === true;

  if (requiresAuth && !authService.isLoggedIn()) {
    return router.createUrlTree(['/guest']);
  }

  // Feature enabled and auth condition satisfied
  return true;
};

/**
 * registrationGuard — protects multi-step registration flow.
 *
 * Checks:
 *   1. Registration has an active temp_code (RegistrationService.current_temp_code
 *      via Dexie, with localStorage 'registerobject' fallback for legacy).
 *   2. Respects disable_nhs_login flag for NHS-based registration branches.
 *
 * When blocked:
 *   - No temp_code → redirect to /postcode-entry (start of registration)
 *   - NHS disabled and route is NHS-only → redirect to /create-account-manual
 *
 * Async because RegistrationService is Dexie-backed (Promise).
 */
export const registrationGuard: CanActivateFn = async (route, state) => {
  const gapsettings = inject(GapSettings);
  const registrationService = inject(RegistrationService);
  const router = inject(Router);

  const isDisabled = isNhsLoginDisabled(gapsettings);

  // NHS-only registration step while NHS is disabled → hand off to manual flow
  const isNhsOnlyStep = route.data?.['nhsOnly'] === true;
  if (isNhsOnlyStep && isDisabled) {
    return router.createUrlTree(['/create-account-manual']);
  }

  // Check active registration temp_code (Dexie)
  let tempCode: string | null = null;
  try {
    tempCode = await registrationService.getCurrentTempCode();
  } catch {
    tempCode = null;
  }

  if (!tempCode) {
    // Legacy fallback: registerobject in localStorage may hold tempRegistrationCode
    try {
      const raw = localStorage.getItem('registerobject');
      if (raw) {
        const parsed = JSON.parse(raw);
        tempCode = parsed?.tempRegistrationCode ?? parsed?.temp_registration_code ?? null;
      }
    } catch {
      tempCode = null;
    }
  }

  // Also allow Dexie registrationSteps fallback via direct localStorage.tempCode if present
  if (!tempCode) {
    const legacyTemp = localStorage.getItem('tempRegistrationCode');
    if (legacyTemp) {
      tempCode = legacyTemp;
    }
  }

  if (!tempCode) {
    return router.createUrlTree(['/postcode-entry']);
  }

  return true;
};

/**
 * lockoutGuard fix note:
 * Original `src/app/guards/lockout.guard.ts` had a bug:
 *   if (data.user?.islocked_out) { navigate; return false; }
 *   return false; // ← bug: always blocks, should be `return true` when not locked.
 * Correct pattern (used here as reference, not exported):
 *   if (data.user?.islocked_out) { router.navigate(['/access-denied']); return false; }
 *   return true;
 * This file does not re-export lockoutGuard — the fix should be applied directly
 * to `lockout.guard.ts`. The note is kept for Task 9 traceability.
 */
